import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { createCheckout, formatPrice } from "../lib/api";
import { toast } from "sonner";

const CartSheet = () => {
  const { items, open, setOpen, totals, updateQuantity, removeItem, itemKey } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          type: i.type,
          slug: i.slug,
          colourway: i.colourway || null,
          size: i.size || null,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      };
      const { url } = await createCheckout(payload);
      window.location.href = url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-chalk border-l border-line p-0 flex flex-col"
        data-testid="cart-sheet"
      >
        <SheetHeader className="px-6 py-5 border-b border-line">
          <SheetTitle className="font-display text-xl uppercase tracking-tight flex items-center justify-between">
            <span>Your Cart</span>
            <span className="mono text-xs text-mute">{totals.count} item{totals.count === 1 ? "" : "s"}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 hide-scrollbar">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty">
              <p className="overline mb-2">Empty Cart</p>
              <p className="text-sm text-mute max-w-xs">
                Pick a colourway. Build your matchday rotation.
              </p>
              <button
                type="button"
                className="btn-primary mt-6"
                onClick={() => {
                  setOpen(false);
                  navigate("/shop");
                }}
                data-testid="cart-empty-shop-button"
              >
                Shop grip socks
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {items.map((item) => {
                const key = itemKey(item);
                return (
                  <li key={key} className="py-4 flex gap-4" data-testid={`cart-item-${item.slug}`}>
                    <div
                      className="w-20 h-20 border border-line flex-shrink-0"
                      style={{ backgroundColor: item.swatch || "#F4F6F8" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium leading-snug">{item.name}</p>
                          <p className="text-xs text-mute mt-1">
                            {item.colourwayLabel ? `${item.colourwayLabel} · ` : ""}
                            {item.size || ""}
                          </p>
                          {item.gripLabel && (
                            <p className="text-[11px] text-mute mt-0.5">Grip · {item.gripLabel}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(key)}
                          className="text-mute hover:text-ink p-1"
                          data-testid={`cart-remove-${item.slug}`}
                          aria-label="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="inline-flex border border-line">
                          <button
                            type="button"
                            onClick={() => updateQuantity(key, item.quantity - 1)}
                            className="w-7 h-7 text-sm hover:bg-line"
                            data-testid={`cart-decrement-${item.slug}`}
                          >
                            −
                          </button>
                          <span className="w-7 h-7 inline-flex items-center justify-center mono text-xs">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(key, item.quantity + 1)}
                            className="w-7 h-7 text-sm hover:bg-line"
                            data-testid={`cart-increment-${item.slug}`}
                          >
                            +
                          </button>
                        </div>
                        <span className="mono text-sm">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-line px-6 py-5 bg-white">
            {!totals.qualifiesFree && totals.remainingForFree > 0 && (
              <div className="mb-4" data-testid="free-shipping-progress">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] tracking-[0.16em] uppercase font-bold">
                    Add {formatPrice(totals.remainingForFree)} for free shipping
                  </p>
                  <span className="mono text-[10px] text-mute">
                    £{(totals.freeThreshold ?? 50).toFixed(0)}
                  </span>
                </div>
                <div className="h-1 bg-line overflow-hidden">
                  <div
                    className="h-full bg-ink transition-all duration-500"
                    style={{
                      width: `${Math.min(100, (totals.subtotal / (totals.freeThreshold || 50)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
            {totals.qualifiesFree && (
              <p
                className="mb-3 text-[11px] tracking-[0.16em] uppercase font-bold text-ink"
                data-testid="free-shipping-unlocked"
              >
                Free UK shipping unlocked ·
              </p>
            )}
            <div className="flex items-center justify-between mb-1.5">
              <span className="overline">Subtotal</span>
              <span className="mono text-sm" data-testid="cart-subtotal">
                {formatPrice(totals.subtotal)}
              </span>
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="overline">Shipping</span>
              <span className="mono text-sm" data-testid="cart-shipping">
                {totals.shipping === 0 ? "Free" : formatPrice(totals.shipping)}
              </span>
            </div>
            <div className="h-px bg-line my-3" />
            <div className="flex items-center justify-between mb-4">
              <span className="overline">Total</span>
              <span className="font-display text-2xl" data-testid="cart-total">
                {formatPrice(totals.total)}
              </span>
            </div>
            <button
              type="button"
              onClick={onCheckout}
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="cart-checkout-button"
            >
              {loading ? "Redirecting…" : "Checkout"}
            </button>
            <p className="mt-3 text-[11px] text-mute text-center mono">
              Secure Stripe checkout · Apple Pay & Google Pay supported
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
